import React, { useEffect, useMemo, useState } from "react";
import { TrendingUp, TrendingDown, HeartPulse, ShieldCheck, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { api, fmtDate, GROUP_COLORS } from "../lib/api";

/**
 * Health Ranking card for Squad Overview.
 *
 * Scoring is intentionally transparent so coaches can defend the order to
 * doctors / parents:
 *
 *   • Start with score = 0 (healthiest).
 *   • For each out-of-range parameter on the athlete's most recent lab,
 *     add min(|deviation_pct|, 50) + 5  (i.e. each flag adds 5–55 points).
 *   • Athletes with no lab on file are surfaced separately as "Unranked".
 *
 * Lower score = healthier. Ties are broken by recency of last lab (more
 * recent first).
 */
export default function HealthRanking({ onSelectSailor }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .get("/squad/issues")
      .then((r) => {
        if (active) setData(r.data);
      })
      .catch((e) => active && setError(e?.response?.data?.detail || "Failed to load ranking"));
    return () => {
      active = false;
    };
  }, []);

  const ranked = useMemo(() => {
    if (!data) return null;
    const scored = (data.athletes || []).map((a) => {
      const score = (a.issues || []).reduce((s, iss) => {
        const dev = Math.min(Math.abs(iss.deviation_pct ?? 0), 50);
        return s + dev + 5;
      }, 0);
      const reason = explain(a, score);
      return { ...a, score, reason };
    });
    // Athletes with lab data → sortable. Athletes without → unranked bucket.
    const withLab = scored.filter((a) => a.latest_lab_date);
    const noLab = scored.filter((a) => !a.latest_lab_date);
    withLab.sort((a, b) => {
      if (a.score !== b.score) return a.score - b.score;
      // Tie-break: more recent lab first (proves we're using fresh data).
      return (b.latest_lab_date || "").localeCompare(a.latest_lab_date || "");
    });
    return { withLab, noLab };
  }, [data]);

  if (error) {
    return (
      <div className="card p-4 text-sm text-rose-200">{error}</div>
    );
  }
  if (!ranked) {
    return (
      <div className="card p-4 text-sm text-slate-500">Compiling health ranking…</div>
    );
  }

  const healthiest = ranked.withLab.slice(0, 5);
  const weakest = [...ranked.withLab].reverse().slice(0, 5);
  const middle = ranked.withLab.slice(5, ranked.withLab.length - 5);

  return (
    <div className="card p-5 md:p-6 fade-up" data-testid="health-ranking-card">
      <div className="flex items-start justify-between mb-1">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-2">
            <HeartPulse size={12} className="text-cyan-300" />
            Squad health ranking
          </div>
          <h3 className="font-display text-lg text-slate-100 mt-0.5">
            Healthiest → Needs attention
          </h3>
        </div>
        <div className="text-[10px] text-slate-500 font-mono-data text-right max-w-[240px] leading-snug">
          Based on the latest lab per athlete. Lower score = healthier.
          Each out-of-range parameter adds 5–55 points by deviation %.
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mt-4">
        {/* Healthiest column */}
        <div data-testid="ranking-healthiest">
          <div className="flex items-center gap-2 mb-2 px-1">
            <ShieldCheck size={14} className="text-emerald-300" />
            <span className="font-display text-sm text-emerald-300">Top 5 Healthiest</span>
          </div>
          <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-lg p-3">
            <ol className="space-y-1.5">
              {healthiest.map((a, idx) => (
                <RankRow key={a.id} athlete={a} rank={idx + 1} accent="#10B981" onSelect={onSelectSailor} />
              ))}
              {healthiest.length === 0 && (
                <div className="text-slate-500 text-sm py-2">No lab data on file yet.</div>
              )}
            </ol>
          </div>
        </div>

        {/* Weakest column */}
        <div data-testid="ranking-weakest">
          <div className="flex items-center gap-2 mb-2 px-1">
            <AlertTriangle size={14} className="text-rose-300" />
            <span className="font-display text-sm text-rose-300">Top 5 Needing Attention</span>
          </div>
          <div className="border border-rose-500/30 bg-rose-500/5 rounded-lg p-3">
            <ol className="space-y-1.5">
              {weakest.map((a, idx) => (
                <RankRow key={a.id} athlete={a} rank={ranked.withLab.length - idx} accent="#EF4444" onSelect={onSelectSailor} />
              ))}
              {weakest.length === 0 && (
                <div className="text-slate-500 text-sm py-2">No lab data on file yet.</div>
              )}
            </ol>
          </div>
        </div>
      </div>

      {(middle.length > 0 || ranked.noLab.length > 0) && (
        <>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="mt-3 text-[11px] text-cyan-300 hover:text-cyan-200 flex items-center gap-1"
            data-testid="ranking-toggle-full"
          >
            {expanded ? (
              <>
                <ChevronUp size={12} /> Hide full ranking
              </>
            ) : (
              <>
                <ChevronDown size={12} /> Show full ranking ({ranked.withLab.length} ranked
                {ranked.noLab.length > 0 ? ` · ${ranked.noLab.length} without lab data` : ""})
              </>
            )}
          </button>

          {expanded && (
            <div className="mt-3 border-t border-[#1E304B] pt-3 space-y-2" data-testid="ranking-expanded">
              {middle.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">
                    Middle of pack
                  </div>
                  <ol className="space-y-1">
                    {middle.map((a, idx) => (
                      <RankRow
                        key={a.id}
                        athlete={a}
                        rank={idx + 6}
                        accent="#94A3B8"
                        onSelect={onSelectSailor}
                        compact
                      />
                    ))}
                  </ol>
                </div>
              )}
              {ranked.noLab.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5 mt-3 flex items-center gap-1">
                    Unranked — no lab record on file
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                    {ranked.noLab.map((a) => (
                      <button
                        key={a.id}
                        onClick={() => onSelectSailor?.(a.id)}
                        className="text-left text-xs px-2 py-1 rounded border border-slate-700/70 text-slate-400 hover:text-slate-200 hover:border-slate-600"
                        data-testid={`ranking-nolab-${a.id}`}
                      >
                        {a.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RankRow({ athlete, rank, accent, onSelect, compact }) {
  const issueCount = athlete.issue_count;
  const labDate = athlete.latest_lab_date;
  return (
    <li
      className="flex items-center gap-3"
      data-testid={`ranking-row-${athlete.id}`}
    >
      <div
        className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-mono-data text-[11px]"
        style={{
          background: `${accent}22`,
          color: accent,
          border: `1px solid ${accent}55`,
        }}
      >
        {rank}
      </div>
      <button
        onClick={() => onSelect?.(athlete.id)}
        className="flex-1 min-w-0 text-left hover:bg-[#0E1A2E] rounded px-1 -mx-1 py-0.5 transition"
        data-testid={`ranking-name-${athlete.id}`}
      >
        <div className="flex items-baseline gap-2">
          <span className="text-slate-100 truncate text-sm">{athlete.name}</span>
          {athlete.subgroup && (
            <span
              className="text-[9px] font-mono-data text-slate-500 truncate"
              style={{ color: GROUP_COLORS[athlete.group] || "#94A3B8" }}
            >
              {athlete.subgroup}
            </span>
          )}
        </div>
        {!compact && (
          <div className="text-[10px] text-slate-400 leading-snug mt-0.5 truncate" title={athlete.reason}>
            {athlete.reason}
          </div>
        )}
      </button>
      <div className="text-right shrink-0">
        <div className="font-mono-data text-xs" style={{ color: accent }}>
          {athlete.score.toFixed(0)}
        </div>
        {!compact && (
          <div className="text-[9px] text-slate-500 font-mono-data">
            {issueCount} flag{issueCount === 1 ? "" : "s"}
            {labDate && <> · {fmtDate(labDate)}</>}
          </div>
        )}
      </div>
    </li>
  );
}

// One-line human explanation of why an athlete sits where they do. Keeps the
// scoring transparent so coaches can defend the order in a clinic.
function explain(a, score) {
  if (!a.latest_lab_date) return "No lab record on file yet.";
  if (a.issue_count === 0) {
    return `All parameters within range (lab: ${fmtDate(a.latest_lab_date)}).`;
  }
  // Surface the two largest deviations.
  const top = (a.issues || []).slice(0, 2).map((iss) => {
    const sign = iss.status === "high" ? "↑" : "↓";
    const pct = iss.deviation_pct != null ? ` ${Math.abs(iss.deviation_pct)}%` : "";
    return `${iss.label} ${sign}${pct}`;
  });
  const more = a.issue_count > 2 ? ` +${a.issue_count - 2} more` : "";
  return `${top.join(", ")}${more}`;
}
